"use client";

import { CustomImage } from '@/components/ui/image';

import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { TypographyH1, TypographyP } from '@/components/ui/typography';
import { Square, CalendarCheck } from 'lucide-react';
import { cn } from '@/lib/utils'; // Assuming cn utility exists
import { motion, useScroll, useTransform } from 'framer-motion';

interface HomeHeroProps {
    title: string;
    subtitle: string;
    backgroundImage: string;
    features: string[];
    tagline: string;
    primaryCta: {
        text: string;
        href: string;
    };
    secondaryCta?: {
        text: string;
        href: string;
        subtext?: string;
    };
}

export function HomeHero({
    title,
    subtitle,
    backgroundImage,
    features,
    tagline,
    primaryCta,
    secondaryCta,
}: HomeHeroProps) {
    const heroRef = useRef<HTMLDivElement>(null);

    // Framer Motion Scroll Hook
    // Observe scroll progress of the hero section itself relative to the viewport
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"]
    });

    // Map scroll progress (0 to 1) to Y transform
    // As we scroll down (progress 0 -> 1), move image down by a percentage
    // slightly slower than scroll to create parallax.
    // E.g., moving from 0% to 15% of the container height.
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

    return (
        <div ref={heroRef} className="relative min-h-[600px] lg:min-h-[750px] bg-[#391d5a] overflow-hidden">
            {/* Background Image with Parallax */}
            <div className="absolute inset-0 w-full h-full overflow-hidden">
                <motion.div
                    className="w-full h-full pointer-events-none"
                    style={{ y }}
                >
                    <CustomImage
                        src={backgroundImage}
                        alt="Hero Background"
                        fill
                        priority
                        className="object-cover opacity-60"
                        containerClassName="w-full h-full absolute top-[-25%] left-0 h-[150%] w-full"
                        withBackground={false}
                        rounded={false}
                    />
                </motion.div>
                {/* Gradient Overlay for better text readability if needed */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#391d5a]/40 to-transparent mix-blend-multiply pointer-events-none" />
            </div>

            {/* Hero Content */}
            <div className="relative z-10 max-w-[1920px] mx-auto px-4 md:px-[60px] lg:px-[101px] pt-32 md:pt-48 lg:pt-[220px]">
                {/* Main Heading */}
                <div className="max-w-[1200px] mb-10 md:mb-16 lg:mb-20 space-y-2">
                    <TypographyH1 className="text-3xl md:text-4xl lg:text-[52px] font-bold text-white leading-tight mb-0 block">
                        CONTROL TELECOM COSTS
                    </TypographyH1>
                    <TypographyH1 className="text-3xl md:text-4xl lg:text-[52px] font-bold text-white leading-tight mb-0 block">
                        with expert audit services
                    </TypographyH1>
                    <TypographyP className="text-xl md:text-2xl lg:text-[32px] text-white leading-normal mt-6 font-normal opacity-90 block">
                        {subtitle}
                    </TypographyP>
                </div>

                {/* Content Grid */}
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center pb-12 relative">
                    {/* White Box with Bullet Points */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 0.9, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="bg-white/90 backdrop-blur-md border border-white/50 rounded-[20px] p-6 lg:p-10 shadow-2xl max-w-[800px] w-full"
                    >
                        <div className="space-y-3 lg:space-y-5">
                            {features.map((feature, index) => (
                                <p key={index} className="flex items-start gap-4 text-[#391d5a] text-lg lg:text-[22px] font-medium leading-snug">
                                    <span className="text-[#8dc63f] mt-[2px] shrink-0 font-bold">✔</span>
                                    <span>{feature}</span>
                                </p>
                            ))}
                        </div>
                    </motion.div>

                    {/* CTA Buttons */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="flex flex-col gap-4 w-full lg:w-fit lg:ml-auto items-start lg:items-center"
                    >
                        <Button
                            size="lg"
                            className="bg-[#8dc63f] border-transparent text-white text-lg md:text-xl lg:text-[22px] font-semibold h-auto py-6 rounded-xl hover:bg-[#7db336] shadow-2xl flex items-center justify-center px-10 gap-4 transition-all hover:scale-[1.02] active:scale-[0.98] w-full lg:w-fit"
                            asChild
                        >
                            <a href={primaryCta.href}>
                                <div className="border-[2px] border-white/90 rounded-md p-1 flex items-center justify-center bg-white/10 backdrop-blur-sm">
                                    <CalendarCheck className="h-5 w-5 text-white" />
                                </div>
                                <span className="tracking-wide pt-[1px]">{primaryCta.text}</span>
                            </a>
                        </Button>
                        {secondaryCta && (
                            <Button
                                size="lg"
                                className="bg-[#8dc63f] border-transparent text-white text-lg md:text-xl lg:text-[22px] font-semibold h-auto py-5 rounded-xl hover:bg-[#7db336] shadow-xl flex items-center justify-center px-10 gap-4 transition-all hover:scale-[1.01] w-full lg:w-fit"
                                asChild
                            >
                                <a href={secondaryCta.href} target="_blank" rel="noopener noreferrer" className="flex items-center">
                                    <div className="border-[2px] border-white/80 rounded-sm p-[2px] mt-1 self-start md:self-center">
                                        <Square className="h-4 w-4 md:h-5 md:w-5 fill-transparent text-white" strokeWidth={3} />
                                    </div>
                                    <div className="text-left leading-tight ml-2">
                                        <span className="block">{secondaryCta.text}</span>
                                        {secondaryCta.subtext && (
                                            <span className="block text-sm md:text-base lg:text-[15px] opacity-90 font-light mt-1">{secondaryCta.subtext}</span>
                                        )}
                                    </div>
                                </a>
                            </Button>
                        )}
                    </motion.div>
                </div>
            </div>
            {/* Tagline Banner - Removing bottom absolute positioning to let it flow naturally if needed, 
                 but keeping absolute as per previous design if it overlaps image? 
                 Actually design shows it completely separate. Let's keep it overlapping bottom of section relative container.
             */}
            {/* Note: The design image doesn't explicitly show the tagline banner at the bottom. 
                 The user prompt "homepage hero should look like this" implies purely the image provided.
                 The provided image shows a dark background with content. 
                 It does NOT show a separate banner strip at the bottom.
                 However, the previous code had it. I will keep it for now but if it looks wrong I'll remove it.
                 Actually, looking at the image, the background goes all the way down. 
                 Let's keep the banner but verify its position.
             */}
        </div>
    );
}
